import { Component, DestroyRef, OnInit, OnDestroy, inject, signal, ChangeDetectorRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormControl, ReactiveFormsModule, ValidatorFn } from '@angular/forms';
import { CardModule } from 'primeng/card';
import { InputNumber } from 'primeng/inputnumber';
import { ButtonModule } from 'primeng/button';
import { Toast } from 'primeng/toast';
import { Tooltip } from 'primeng/tooltip';
import { MessageService, ConfirmationService } from 'primeng/api';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { FilingFormStateService } from '../../../services/mbrs/filing-form-state.service';
import { CrossReferenceService } from '../../../services/mbrs/cross-reference.service';
import { CalculationService } from '../../../services/mbrs/calculation.service';
import { CrossTabLayoutChangeNotifierService } from '../../../services/mbrs/cross-tab-layout-change-notifier.service';
import { EnvironmentService } from '../../../services/common/environment.service';
import { RuntimeConfigService } from '../../../services/common/runtime-config.service';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, takeUntil } from 'rxjs/operators';
import { NegativeHighlightDirective } from '../../../directives/negative-highlight.directive';
import { SchemaNumericDirective } from '../../../directives/schema-numeric.directive';
import { NumericFieldPolicy, resolveNumericPolicy } from '../../../utils/numeric-policy.util';
import { Router } from '@angular/router';
import { ReloadPromptComponent } from '../../common/reload-prompt/reload-prompt.component';
import { ReloadBannerComponent } from '../../common/reload-banner/reload-banner.component';
import { stripAllValidatorsRecursively } from '../../../utils/form-validation.util';
import {
  CROSS_SHEET_VALIDATION_DEBOUNCE_MS,
  FORM_CALCULATION_DEBOUNCE_MS,
} from '../../../utils/debounce-times.util';
import { buildFormEditPipeline } from '../../../utils/form-edit-pipeline.util';
import {
  resolveRole17ColumnSuffixes,
  resolveRole17LayoutFlags,
  Role17ColumnSuffix,
} from '../../../services/mbrs/validators/role17-cash-flows-direct.rules';
import { SHEET_KEYS } from '../../../models/sheet-keys';
import { ExtractedSumWarningService } from '../../../services/mbrs/extracted-sum-warning.service';
import { SummableControl } from '../../../services/mbrs/extracted-sum-warning';
import { ExtractedSumBaselineDebugService } from '../../../services/common/extracted-sum-baseline-debug.service';
import { isControlMandatory } from '../../../utils/required-field.util';
import { RequiredMarkerComponent } from '../../common/required-marker/required-marker.component';
import { SheetLoaderComponent } from '../../common/sheet-loader/sheet-loader.component';
import { FieldLinkTargetDirective } from '../../../directives/field-link-target.directive';
import {
  TOAST_DURATION_DEFAULT_MS,
  TOAST_DURATION_LONG_MS,
} from '../../../utils/toast-durations.util';
import { computedControlPredicate, revealValidationErrors } from '../../../utils/validation-reveal.util';
import { adoptIntoAutoSaveSheet } from '../../../utils/adopt-remote-sheet-state.util';
import {
  LoadTrace,
  NO_OP_LOAD_TRACE,
  SheetLoadTraceService,
} from '../../../services/mbrs/sheet-load-trace.service';
import { SHEET_LOAD_STEPS } from '../../../utils/sheet-load-trace.util';
import {
  classifySheetInitError,
  fetchSheetSchema,
} from '../../../utils/schema-asset.util';

interface ControlConfig {
  qname: string;
  label: string;
  type: string;
  ui: {
    control: string;
    level?: number;
    currency?: string;
    placeholder?: string;
    dualColumn?: boolean;
    calculated?: boolean;
    note?: string;
  };
}

interface SchemaConfig {
  role: string;
  sheetId: string;
  label: string;
  description: string;
  controls: ControlConfig[];
}

@Component({
  selector: 'app-role17-510000-statement-of-cash-flows-direct',
  standalone: true,
  imports: [
    SheetLoaderComponent,
    RequiredMarkerComponent,
    CommonModule,
    ReactiveFormsModule,
    CardModule,
    InputNumber,
    ButtonModule,
    Toast,
    Tooltip,
    ConfirmDialog,
    NegativeHighlightDirective,

    SchemaNumericDirective,
    ReloadPromptComponent,
    ReloadBannerComponent,
    FieldLinkTargetDirective
  ],
  providers: [MessageService, ConfirmationService],
  templateUrl: './role17-510000-statement-of-cash-flows-direct.component.html',
  styleUrls: ['./role17-510000-statement-of-cash-flows-direct.component.css']
})
// Developer Note (TODO): NOT migrated to the shared MappingStatusService (Mapping-Status
// "Edited vs Validated" refactor, 2026-05). This sheet is not in active support/use, AND its
// `originalState` is not populated by filing-form-container (no `populate…FromMbrsData` /
// `loadFromAPI('statementOfCashFlowsDirectMethod', …)`), so deriving "Edited" from the
// last-submission baseline would mark every row Edited. (Its validateAgainstApiData is also
// currently stubbed/under-review.) When this sheet is revived:
//   1. Add a `populateStatementOfCashFlowsDirectFromMbrsData` path in filing-form-container
//      (so getOriginalSheetData returns real data), and add its tuple to populateSnapshotFromMbrsData.
//   2. Then apply the standard migration: delegate getRowValidationStatus to MappingStatusService,
//      re-source validateAgainstApiData from getSnapshotValues, delete validationBaseline + editedRows.
// Until then it keeps its legacy per-sheet validationBaseline/editedRows logic.
export class Role17510000StatementOfCashFlowsDirectComponent implements OnInit, OnDestroy {
  form!: FormGroup;
  schema!: SchemaConfig;
  isLoading = true;
  isConsolidated = false;
  hasPreviousYear = false;

  // Dynamic form title with Type of Submission
  /**
   * Card header carrying the filing's submission type, e.g.
   * "Statement of Cash Flows (Direct Method) (FS-MPERS)".
   *
   * Two rules, both of which this sheet used to break:
   *  - the name comes from the canonical `SHEET_DISPLAY_NAMES` map (via
   *    `getSheetTitleWithSubmissionType`), never a literal repeated here;
   *  - it is a LIVE getter, never an `ngOnInit` snapshot — see the cold-mount
   *    rule on `FilingFormStateService.getFormTitleWithSubmissionType()`.
   */
  get formTitle(): string {
    return this.stateService.getSheetTitleWithSubmissionType(
      SHEET_KEYS.statementOfCashFlowsDirectMethod
    );
  }

  private destroy$ = new Subject<void>();
  private destroyRef = inject(DestroyRef);
  private stateService = inject(FilingFormStateService);
  private crossRefService = inject(CrossReferenceService);
  private calculationService = inject(CalculationService);
  private layoutChangeNotifier = inject(CrossTabLayoutChangeNotifierService);
  private messageService = inject(MessageService);
  private loadTrace = inject(SheetLoadTraceService);
  /**
   * This mount's load trace. Opened as the first act of `ngOnInit` and closed
   * either by the terminal step mark or by the init catch, so a failure reports
   * which of the seven load steps this sheet actually reached.
   *
   * @see src/app/utils/sheet-load-trace.util.ts
   */
  private loadTraceHandle: LoadTrace = NO_OP_LOAD_TRACE;
  private confirmationService = inject(ConfirmationService);
  private cdr = inject(ChangeDetectorRef);
  private envService = inject(EnvironmentService);
  private runtimeConfig = inject(RuntimeConfigService);
  private extractedSumWarning = inject(ExtractedSumWarningService);
  private baselineDebug = inject(ExtractedSumBaselineDebugService);
  readonly fieldNoteTooltipsEnabled = this.runtimeConfig.fieldNoteTooltipsEnabled;

  private readonly sheetKey = SHEET_KEYS.statementOfCashFlowsDirectMethod;

  showReloadPrompt = signal<boolean>(false);
  reloadPromptDismissed = signal<boolean>(false);

  // Validation baseline for warnings (persisted to localStorage)
  private validationBaseline: Record<string, any> | null = null;
  private validationErrors = new Map<string, string[]>();

  /**
   * Get validation baseline key with conversionId scope
   */
  private get VALIDATION_BASELINE_KEY(): string {
    return `mbrs-validation-baseline-role17-${this.stateService.getConversionId()}`;
  }

  /**
   * Get currency symbol with unit from Role-1
   * e.g., "RM'000", "000", "€'000"
   */
  get currencyUnit(): string {
    const symbol = this.crossRefService.getCurrencySymbol();
    return `${symbol}'000`;
  }

  /**
   * Get period header for current year
   * e.g., "For the year ended 31 December 2024"
   */
  get periodHeaderCurrent(): string {
    return this.crossRefService.getPeriodHeader('current');
  }

  /**
   * Get period header for previous year
   * e.g., "For the year ended 31 December 2023"
   */
  get periodHeaderPrevious(): string {
    return this.crossRefService.getPeriodHeader('previous');
  }

  constructor(private fb: FormBuilder) {}

  ngOnInit(): void {
    // Open this mount's load trace before anything else, so a failure
    // anywhere below reports which load steps were reached.
    this.loadTraceHandle = this.loadTrace.beginSheetLoad(
      '17',
      this.stateService.getConversionId(),
      this.destroyRef,
    );
    // This sheet does not await the container's MBRS hydration, so step 1
    // has nothing to wait for and is satisfied on arrival.
    this.loadTraceHandle.mark(SHEET_LOAD_STEPS.hydrationSettled);
    this.baselineDebug.bindGlobalShortcut(this.destroyRef);
    this.checkConsolidatedStatus();
    this.loadSchema(); // This will call buildForm() after schema loads
    this.loadValidationBaseline(); // Load validation baseline from localStorage
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Load validation baseline from localStorage
   */
  private loadValidationBaseline(): void {
    try {
      const stored = localStorage.getItem(this.VALIDATION_BASELINE_KEY);
      if (stored) {
        this.validationBaseline = JSON.parse(stored);
        // Debug Log (Only enable when required to debug, do not allow this to land into prod)
        // console.log('💾 Validation baseline loaded from localStorage');
      }
    } catch (error) {
      console.error('Error loading validation baseline from localStorage:', error);
      this.validationBaseline = null;
    }
  }

  /**
   * Save validation baseline to localStorage
   */
  private saveValidationBaseline(): void {
    try {
      if (this.validationBaseline) {
        localStorage.setItem(
          this.VALIDATION_BASELINE_KEY,
          JSON.stringify(this.validationBaseline)
        );
        // Debug Log (Only enable when required to debug, do not allow this to land into prod)
        // console.log('💾 Validation baseline saved to localStorage');
      }
    } catch (error) {
      console.error('Error saving validation baseline to localStorage:', error);
    }
  }

  /**
   * Clear validation baseline from localStorage (for cleanup)
   */
  clearValidationBaseline(): void {
    try {
      localStorage.removeItem(this.VALIDATION_BASELINE_KEY);
      this.validationBaseline = null;
      this.validationErrors.clear();
      // Debug Log (Only enable when required to debug, do not allow this to land into prod)
      // console.log('🗑️ Validation baseline cleared');
    } catch (error) {
      console.error('Error clearing validation baseline:', error);
    }
  }

  /**
   * Check consolidation status and comparative information from Role-2.
   * Single source of truth for the flag-resolution logic is
   * `resolveRole17LayoutFlags` in the shared rules file — mirrors role 11
   * and keeps the pure validator + editor in lockstep.
   */
  private checkConsolidatedStatus(): void {
    const scopeOfFilingData = this.stateService.getSheetData(SHEET_KEYS.scopeOfFiling);
    const { isConsolidated, hasPreviousYear } =
      resolveRole17LayoutFlags(scopeOfFilingData as Record<string, unknown> | undefined);
    this.isConsolidated = isConsolidated;
    this.hasPreviousYear = hasPreviousYear;

    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // console.log('Role-17 Column Configuration:', {
    //   isConsolidated: this.isConsolidated,
    //   hasPreviousYear: this.hasPreviousYear,
    //   expectedColumns: this.getColumnCount()
    // });
  }

  // Why: pure predicate, no mutation of isConsolidated / hasPreviousYear.
  // Under the reload-prompt model the component's layout MUST stay frozen
  // until the user reloads — mutating the flags here would break the
  // schema/data invariant the cached FormGroup relies on. Compare-only,
  // return boolean, let the notifier surface the prompt.
  private detectLayoutFlagsChanged(): boolean {
    const scopeOfFilingData = this.stateService.getSheetData(SHEET_KEYS.scopeOfFiling);
    const { isConsolidated: freshConsolidated, hasPreviousYear: freshPreviousYear } =
      resolveRole17LayoutFlags(scopeOfFilingData as Record<string, unknown> | undefined);

    return (
      freshConsolidated !== this.isConsolidated ||
      freshPreviousYear !== this.hasPreviousYear
    );
  }

  /**
   * Visible column suffixes for the current (isConsolidated, hasPreviousYear)
   * pair. Delegates to the shared rules file so the suffix ladder has ONE
   * source of truth (the component previously duplicated the branch ladder
   * in `buildForm()`, `calculateTotals()`, `validateAgainstApiData()`,
   * `getRowValidationStatus()`, `getRowErrorDescription()`, and
   * `autoFillDemo()`).
   */
  private getColumnSuffixes(): ReadonlyArray<Role17ColumnSuffix> {
    return resolveRole17ColumnSuffixes(this.isConsolidated, this.hasPreviousYear);
  }

  private router = inject(Router);

  private setupLayoutReactivity(): void {
    this.layoutChangeNotifier
      .watchSubmissionCommitted({
        sheetKey: this.sheetKey,
        slicesOfInterest: [SHEET_KEYS.filingPrerequisites, SHEET_KEYS.filingInfo, SHEET_KEYS.scopeOfFiling],
        detectChanged: () => this.detectLayoutFlagsChanged(),
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (!this.reloadPromptDismissed()) {
          this.showReloadPrompt.set(true);
        }
      });
  }

  onReload(): void {
    window.location.reload();
  }

  onDismiss(): void {
    this.showReloadPrompt.set(false);
    this.reloadPromptDismissed.set(true);
    this.disableAllValidations();
  }

  onReturnToFiles(): void {
    this.showReloadPrompt.set(false);
    this.router.navigate(['/mbrs/xbrl-files']);
  }

  // Why: one-way door. Once the user dismisses the reload prompt the
  // in-memory form schema is known-stale vs. upstream scope; persisting
  // any further validation result would be misleading. We strip every
  // validator from every control (and the root FormGroup) so no spurious
  // errors accumulate before the user reloads. There is deliberately no
  // re-enable path — a fresh ngOnInit (via reload) is the only recovery.
  private disableAllValidations(): void {
    stripAllValidatorsRecursively(this.form);
  }

  /**
   * Get number of columns to display
   */
  getColumnCount(): number {
    if (this.isConsolidated && this.hasPreviousYear) return 4;
    if (this.isConsolidated || this.hasPreviousYear) return 2;
    return 1;
  }

  /**
   * Get colspan for section headers (includes description column)
   */
  getColspan(): number {
    return this.getColumnCount() + 1; // +1 for description column
  }

  /**
   * Load schema from JSON file
   */
  private loadSchema(): void {
    this.loadTraceHandle.mark(SHEET_LOAD_STEPS.schemaSelected);
    fetchSheetSchema<SchemaConfig>(
        this.loadTraceHandle,
        'assets/forms/role-510000.form.json',
      )
      .then(data => {
        this.schema = data;
        this.loadTraceHandle.mark(SHEET_LOAD_STEPS.schemaParsed);
        // Debug Log (Only enable when required to debug, do not allow this to land into prod)
        // console.log('Role-17 schema loaded:', this.schema);

        // Now that schema is loaded, build form and initialize
        this.buildForm();
        this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.formBuilt);
        this.loadSavedState();
        this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.stateRestored);
        this.setupEditPipeline();

        // Reveal the sheet's already-computed errors now that the form exists
        // and has been populated. Deterministic replacement for the filing-form
        // container's setTimeout-driven markAllAsTouched, which silently no-ops
        // when a sheet's async init has not yet assigned its FormGroup — see
        // revealValidationErrors' docblock. Touch-only: cannot move the verdict.
        revealValidationErrors(this.form, {
          isComputed: computedControlPredicate(this.schema?.controls),
        });
        this.setupStatePersistence();
        this.setupLayoutReactivity();
        this.isLoading = false;
        this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.finalized);

        // Snapshot baseline after all debounced valueChanges settle
        setTimeout(() => {
          this.stateService.markSheetInitializedWithStatus(SHEET_KEYS.statementOfCashFlowsDirectMethod, false);
        }, 600);
      })
      .catch(error => {
        console.error('Error loading schema:', error);
        // Classified rather than reported as a bare schema failure: this one
        // `.catch` also covers the cross-sheet preload, buildForm, the edit
        // pipeline and loadSavedState, so "Failed to load form schema" named
        // the wrong step for most of what can land here.
        this.loadTraceHandle.fail(classifySheetInitError(error), error);
        this.isLoading = false;
        this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.finalized);
      });
  }

  /**
   * Build reactive form based on schema and column configuration
   */
  private buildForm(): void {
    const formControls: any = {};
    const validators: ValidatorFn[] = [];

    this.schema?.controls.forEach(control => {
      if (control.ui.control === 'header') {
        return; // Skip headers
      }

      if (control.ui.dualColumn) {
        // Use null for non-calculated fields, 0 for calculated/total fields
        // This ensures unmapped API data fields remain as NULL (not converted to 0)
        const defaultValue = control.ui.calculated ? 0 : null;

        // Scenario 1: Both consolidated and previous year (4 columns)
        if (this.isConsolidated && this.hasPreviousYear) {
          formControls[control.qname + 'GroupCurrent'] = new FormControl(
            { value: defaultValue, disabled: !!control.ui.calculated },
            validators
          );
          formControls[control.qname + 'GroupPrevious'] = new FormControl(
            { value: defaultValue, disabled: !!control.ui.calculated },
            validators
          );
          formControls[control.qname + 'CompanyCurrent'] = new FormControl(
            { value: defaultValue, disabled: !!control.ui.calculated },
            validators
          );
          formControls[control.qname + 'CompanyPrevious'] = new FormControl(
            { value: defaultValue, disabled: !!control.ui.calculated },
            validators
          );
        }
        // Scenario 2: Consolidated only (2 columns - Group/Company for current year)
        else if (this.isConsolidated && !this.hasPreviousYear) {
          formControls[control.qname + 'GroupCurrent'] = new FormControl(
            { value: defaultValue, disabled: !!control.ui.calculated },
            validators
          );
          formControls[control.qname + 'CompanyCurrent'] = new FormControl(
            { value: defaultValue, disabled: !!control.ui.calculated },
            validators
          );
        }
        // Scenario 3: Previous year only (2 columns - Current/Previous for company)
        else if (!this.isConsolidated && this.hasPreviousYear) {
          formControls[control.qname + 'Current'] = new FormControl(
            { value: defaultValue, disabled: !!control.ui.calculated },
            validators
          );
          formControls[control.qname + 'Previous'] = new FormControl(
            { value: defaultValue, disabled: !!control.ui.calculated },
            validators
          );
        }
        // Scenario 4: Neither (1 column - Current only)
        else {
          formControls[control.qname + 'Current'] = new FormControl(
            { value: defaultValue, disabled: !!control.ui.calculated },
            validators
          );
        }
      }
    });

    this.form = new FormGroup(formControls);
    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // console.log('Role-17 form built with controls:', Object.keys(formControls).length);
  }

  /**
   * Wire the single debounced edit pipeline: calc → persist → validate.
   * Guard mirrors the old calc sub: skip while pre-validating.
   */
  private setupEditPipeline(): void {
    buildFormEditPipeline({
      source$: this.form.valueChanges,
      debounceMs: FORM_CALCULATION_DEBOUNCE_MS,
      destroyRef: this.destroyRef,
      shouldSkip: () => this.stateService.isPreValidating(),
      calculate: () => this.calculateTotals(),
      persist: () => this.stateService.updateSheetData(
        SHEET_KEYS.statementOfCashFlowsDirectMethod,
        this.form.value
      ),
      validate: () => this.runAllValidations(),
    });
  }

  /**
   * Calculate all totals
   */
  private calculateTotals(): void {
    const suffixes = this.getColumnSuffixes();

    const calculatedTotals = this.calculationService.calculateTotals_510000(
      this.form.getRawValue(),
      suffixes
    );

    Object.keys(calculatedTotals).forEach((controlName) => {
      this.form.get(controlName)?.setValue(calculatedTotals[controlName], { emitEvent: false });
    });

    this.stateService.registerCalculatedKeys(
      SHEET_KEYS.statementOfCashFlowsDirectMethod,
      Object.keys(calculatedTotals)
    );
  }

  /**
   * Load saved state from localStorage
   */
  private loadSavedState(): void {
    this.stateService.markSheetInitializing(SHEET_KEYS.statementOfCashFlowsDirectMethod);
    const savedData = this.stateService.getSheetData(SHEET_KEYS.statementOfCashFlowsDirectMethod);
    if (savedData && Object.keys(savedData).length > 0) {
      this.form.patchValue(savedData, { emitEvent: false });
      // Debug Log (Only enable when required to debug, do not allow this to land into prod)
      // console.log('Role-17 state loaded from localStorage');

      // Set validation baseline if not already set (one-time setup)
      if (!this.validationBaseline || Object.keys(this.validationBaseline).length === 0) {
        // Debug Log (Only enable when required to debug, do not allow this to land into prod)
        // console.log('🎯 Setting validation baseline from saved data');
        this.validationBaseline = { ...savedData };
        this.saveValidationBaseline();
      }

      // Recalculate after loading
      setTimeout(() => {
        this.calculateTotals();
        this.runAllValidations();
      }, 100);
    } else {
      // Report sheet-level status even when no saved data
      setTimeout(() => {
      }, 100);
    }
  }

  /**
   * Setup state persistence to localStorage
   * Skips processing during pre-validation to prevent infinite loops
   */
  private setupStatePersistence(): void {
    // ----------------------------------
    // Cross-sheet local re-validation watch (golden pattern, role-19).
    //
    // Role-17 reads scopeOfFiling @ L178 (consolidation toggle controls
    // column scenario) and filingPrerequisites (currency, taxonomy variant
    // affecting calculations). Same-tab SPA navigation back to this sheet
    // should trigger re-validation when either upstream slice has changed.
    this.stateService.modifiedState$
      .pipe(
        filter(() => !this.stateService.isSheetInitializing(SHEET_KEYS.statementOfCashFlowsDirectMethod)),
        filter(() => !this.stateService.isPreValidating()),
        map(state => JSON.stringify({
          r0: state.filingPrerequisites,
          r1: state.filingInfo,
          r2: state.scopeOfFiling,
        })),
        distinctUntilChanged(),
        debounceTime(CROSS_SHEET_VALIDATION_DEBOUNCE_MS),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.runAllValidations();
      });
    // ----------------------------------

    // ----------------------------------
    // Cross-tab safety net — sibling-tab edits to filingPrerequisites or
    // scopeOfFiling must trigger re-validation here too.
    this.stateService.remoteUpdateApplied$
      .pipe(
        filter(({ streamKey }) => streamKey === 'modifiedState'),
        filter(() => !this.stateService.isSheetInitializing(SHEET_KEYS.statementOfCashFlowsDirectMethod)),
        filter(() => !this.stateService.isPreValidating()),
        filter(() => !!this.schema && !!this.form),
        takeUntil(this.destroy$)
      )
      .subscribe(({ sheetKey }) => {
        // Debug Log (Only enable when required to debug, do not allow this to land into prod)
        if (this.envService.isCrossTabSyncDebugEnabled()) {
          // console.log('[CrossTabSync][remote-validation-triggered]', {
          //   receiver: 'role17-statementOfCashFlowsDirectMethod',
          //   sourceSheetKey: sheetKey,
          // });
        }
        this.runAllValidations();
      });
    // ----------------------------------

    // ----------------------------------
    // Cross-tab live adoption — when a sibling tab edits THIS sheet's own
    // statementOfCashFlowsDirectMethod slice, `applyRemoteChange` rehydrates
    // the shared state and publishes `changedSheets` on
    // `remoteUpdateApplied$`. `adoptRemoteState()` below repaints this
    // sheet's form from that state (silently, no emitted valueChanges).
    //
    // Deliberately a SEPARATE subscription from the cross-tab safety net
    // above, but that safety net does NOT filter on `changedSheets` — it has
    // no slice filter at all — so it fires on EVERY remote `modifiedState`
    // update, including one that changed THIS sheet's own slice. It is
    // registered first, so on an own-slice remote update it runs
    // `runAllValidations()` once against the still-STALE, pre-adoption form,
    // then this subscription runs `adoptRemoteState()`. The end state is
    // correct; the intermediate evaluation between the two is wrong-valued
    // for one microtask. Deliberately NOT deduped — deduping risks dropping
    // the upstream-driven refresh the safety net exists for
    // (filingPrerequisites/scopeOfFiling slice changes that never touch
    // `changedSheets` for this sheet at all); see fix-wave I4.
    this.stateService.remoteUpdateApplied$
      .pipe(
        filter(({ streamKey }) => streamKey === 'modifiedState'),
        filter(({ changedSheets }) => changedSheets.includes(SHEET_KEYS.statementOfCashFlowsDirectMethod)),
        filter(() => !this.stateService.isSheetInitializing(SHEET_KEYS.statementOfCashFlowsDirectMethod)),
        filter(() => !this.stateService.isPreValidating()),
        filter(() => !!this.schema && !!this.form),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        // Debug Log (Only enable when required to debug, do not allow this to land into prod)
        if (this.envService.isCrossTabSyncDebugEnabled()) {
          // console.log('[CrossTabSync][remote-validation-triggered]', {
          //   receiver: 'role17-statementOfCashFlowsDirectMethod',
          // });
        }
        this.adoptRemoteState();
      });
    // ----------------------------------
  }

  /**
   * Cross-tab adoption: a sibling tab edited this sheet, `applyRemoteChange` has
   * already rehydrated the shared state, and this repaints the form from it.
   *
   * MUST NOT write back to state — see the invariant on `adoptIntoAutoSaveSheet`.
   * The validation tail below runs for DISPLAY only; the sibling already
   * persisted its own results.
   *
   * Developer Note (TODO): silent adoption is the P.O. decision of 2026-08-17 and
   * is revisitable. If feedback is later wanted, the two options considered were a
   * brief per-row highlight (preferred — it answers "why did that number change?"
   * at the spot it changed) and a per-adoption toast (cheaper, but says nothing
   * about WHAT changed and stacks on repeated sibling edits).
   */
  private adoptRemoteState(): void {
    adoptIntoAutoSaveSheet(this.form, this.stateService.getSheetData(SHEET_KEYS.statementOfCashFlowsDirectMethod));
    this.runAllValidations();
  }

  /**
   * Auto-fill with demo data
   */
  autoFillDemo(): void {
    const demoData: any = {};

    // Demo data for each scenario
    if (this.isConsolidated && this.hasPreviousYear) {
      // 4 columns
      const suffixes = ['GroupCurrent', 'GroupPrevious', 'CompanyCurrent', 'CompanyPrevious'];
      const multiplier = [1.0, 0.9, 0.95, 0.85];

      suffixes.forEach((suffix, index) => {
        const mult = multiplier[index];

        // Operating activities
        demoData['ssmt:CashReceiptsFromCustomers' + suffix] = Math.round(52000000 * mult);
        demoData['ssmt:CashPaidToSuppliersAndEmployees' + suffix] = Math.round(42000000 * mult);
        demoData['ssmt:InterestPaid' + suffix] = Math.round(800000 * mult);
        demoData['ssmt:IncomeTaxesPaid' + suffix] = Math.round(1800000 * mult);
        demoData['ssmt:InterestReceived' + suffix] = Math.round(150000 * mult);
        demoData['ssmt:DividendsReceived' + suffix] = Math.round(100000 * mult);

        // Investing activities
        demoData['ssmt:PurchaseOfPropertyPlantAndEquipment' + suffix] = Math.round(3500000 * mult);
        demoData['ssmt:ProceedsFromDisposalOfPropertyPlantAndEquipment' + suffix] = Math.round(200000 * mult);
        demoData['ssmt:PurchaseOfIntangibleAssets' + suffix] = Math.round(500000 * mult);
        demoData['ssmt:PurchaseOfInvestments' + suffix] = Math.round(1000000 * mult);
        demoData['ssmt:ProceedsFromDisposalOfInvestments' + suffix] = Math.round(300000 * mult);
        demoData['ssmt:AcquisitionOfSubsidiaries' + suffix] = Math.round(2000000 * mult);

        // Financing activities
        demoData['ssmt:ProceedsFromIssuanceOfShareCapital' + suffix] = Math.round(5000000 * mult);
        demoData['ssmt:ProceedsFromBorrowings' + suffix] = Math.round(8000000 * mult);
        demoData['ssmt:RepaymentOfBorrowings' + suffix] = Math.round(6000000 * mult);
        demoData['ssmt:PaymentOfLeaseLiabilities' + suffix] = Math.round(400000 * mult);
        demoData['ssmt:DividendsPaid' + suffix] = Math.round(2000000 * mult);
        demoData['ssmt:DividendsPaidToNonControllingInterests' + suffix] = Math.round(50000 * mult);

        // Cash reconciliation
        demoData['ssmt:CashAndCashEquivalentsAtBeginningOfYear' + suffix] = Math.round(5000000 * mult);
        demoData['ssmt:EffectOfExchangeRateChanges' + suffix] = Math.round(-50000 * mult);
      });
    } else if (this.isConsolidated && !this.hasPreviousYear) {
      // 2 columns: GroupCurrent, CompanyCurrent
      const suffixes = ['GroupCurrent', 'CompanyCurrent'];
      const multiplier = [1.0, 0.95];

      suffixes.forEach((suffix, index) => {
        const mult = multiplier[index];

        demoData['ssmt:CashReceiptsFromCustomers' + suffix] = Math.round(52000000 * mult);
        demoData['ssmt:CashPaidToSuppliersAndEmployees' + suffix] = Math.round(42000000 * mult);
        demoData['ssmt:InterestPaid' + suffix] = Math.round(800000 * mult);
        demoData['ssmt:IncomeTaxesPaid' + suffix] = Math.round(1800000 * mult);
        demoData['ssmt:InterestReceived' + suffix] = Math.round(150000 * mult);
        demoData['ssmt:DividendsReceived' + suffix] = Math.round(100000 * mult);
        demoData['ssmt:PurchaseOfPropertyPlantAndEquipment' + suffix] = Math.round(3500000 * mult);
        demoData['ssmt:ProceedsFromDisposalOfPropertyPlantAndEquipment' + suffix] = Math.round(200000 * mult);
        demoData['ssmt:PurchaseOfIntangibleAssets' + suffix] = Math.round(500000 * mult);
        demoData['ssmt:PurchaseOfInvestments' + suffix] = Math.round(1000000 * mult);
        demoData['ssmt:ProceedsFromDisposalOfInvestments' + suffix] = Math.round(300000 * mult);
        demoData['ssmt:AcquisitionOfSubsidiaries' + suffix] = Math.round(2000000 * mult);
        demoData['ssmt:ProceedsFromIssuanceOfShareCapital' + suffix] = Math.round(5000000 * mult);
        demoData['ssmt:ProceedsFromBorrowings' + suffix] = Math.round(8000000 * mult);
        demoData['ssmt:RepaymentOfBorrowings' + suffix] = Math.round(6000000 * mult);
        demoData['ssmt:PaymentOfLeaseLiabilities' + suffix] = Math.round(400000 * mult);
        demoData['ssmt:DividendsPaid' + suffix] = Math.round(2000000 * mult);
        demoData['ssmt:DividendsPaidToNonControllingInterests' + suffix] = Math.round(50000 * mult);
        demoData['ssmt:CashAndCashEquivalentsAtBeginningOfYear' + suffix] = Math.round(5000000 * mult);
        demoData['ssmt:EffectOfExchangeRateChanges' + suffix] = Math.round(-50000 * mult);
      });
    } else if (!this.isConsolidated && this.hasPreviousYear) {
      // 2 columns: Current, Previous
      const suffixes = ['Current', 'Previous'];
      const multiplier = [1.0, 0.9];

      suffixes.forEach((suffix, index) => {
        const mult = multiplier[index];

        demoData['ssmt:CashReceiptsFromCustomers' + suffix] = Math.round(50000000 * mult);
        demoData['ssmt:CashPaidToSuppliersAndEmployees' + suffix] = Math.round(40000000 * mult);
        demoData['ssmt:InterestPaid' + suffix] = Math.round(750000 * mult);
        demoData['ssmt:IncomeTaxesPaid' + suffix] = Math.round(1700000 * mult);
        demoData['ssmt:InterestReceived' + suffix] = Math.round(120000 * mult);
        demoData['ssmt:DividendsReceived' + suffix] = Math.round(80000 * mult);
        demoData['ssmt:PurchaseOfPropertyPlantAndEquipment' + suffix] = Math.round(3200000 * mult);
        demoData['ssmt:ProceedsFromDisposalOfPropertyPlantAndEquipment' + suffix] = Math.round(150000 * mult);
        demoData['ssmt:PurchaseOfIntangibleAssets' + suffix] = Math.round(400000 * mult);
        demoData['ssmt:PurchaseOfInvestments' + suffix] = Math.round(800000 * mult);
        demoData['ssmt:ProceedsFromDisposalOfInvestments' + suffix] = Math.round(250000 * mult);
        demoData['ssmt:AcquisitionOfSubsidiaries' + suffix] = 0;
        demoData['ssmt:ProceedsFromIssuanceOfShareCapital' + suffix] = Math.round(3000000 * mult);
        demoData['ssmt:ProceedsFromBorrowings' + suffix] = Math.round(7000000 * mult);
        demoData['ssmt:RepaymentOfBorrowings' + suffix] = Math.round(5500000 * mult);
        demoData['ssmt:PaymentOfLeaseLiabilities' + suffix] = Math.round(350000 * mult);
        demoData['ssmt:DividendsPaid' + suffix] = Math.round(1500000 * mult);
        demoData['ssmt:DividendsPaidToNonControllingInterests' + suffix] = 0;
        demoData['ssmt:CashAndCashEquivalentsAtBeginningOfYear' + suffix] = Math.round(4500000 * mult);
        demoData['ssmt:EffectOfExchangeRateChanges' + suffix] = Math.round(-40000 * mult);
      });
    } else {
      // 1 column: Current only
      demoData['ssmt:CashReceiptsFromCustomersCurrent'] = 50000000;
      demoData['ssmt:CashPaidToSuppliersAndEmployeesCurrent'] = 40000000;
      demoData['ssmt:InterestPaidCurrent'] = 750000;
      demoData['ssmt:IncomeTaxesPaidCurrent'] = 1700000;
      demoData['ssmt:InterestReceivedCurrent'] = 120000;
      demoData['ssmt:DividendsReceivedCurrent'] = 80000;
      demoData['ssmt:PurchaseOfPropertyPlantAndEquipmentCurrent'] = 3200000;
      demoData['ssmt:ProceedsFromDisposalOfPropertyPlantAndEquipmentCurrent'] = 150000;
      demoData['ssmt:PurchaseOfIntangibleAssetsCurrent'] = 400000;
      demoData['ssmt:PurchaseOfInvestmentsCurrent'] = 800000;
      demoData['ssmt:ProceedsFromDisposalOfInvestmentsCurrent'] = 250000;
      demoData['ssmt:AcquisitionOfSubsidiariesCurrent'] = 0;
      demoData['ssmt:ProceedsFromIssuanceOfShareCapitalCurrent'] = 3000000;
      demoData['ssmt:ProceedsFromBorrowingsCurrent'] = 7000000;
      demoData['ssmt:RepaymentOfBorrowingsCurrent'] = 5500000;
      demoData['ssmt:PaymentOfLeaseLiabilitiesCurrent'] = 350000;
      demoData['ssmt:DividendsPaidCurrent'] = 1500000;
      demoData['ssmt:DividendsPaidToNonControllingInterestsCurrent'] = 0;
      demoData['ssmt:CashAndCashEquivalentsAtBeginningOfYearCurrent'] = 4500000;
      demoData['ssmt:EffectOfExchangeRateChangesCurrent'] = -40000;
    }

    this.form.patchValue(demoData);
    this.messageService.add({
      severity: 'success',
      summary: 'Demo Data Loaded',
      detail: 'Statement of Cash Flows (Direct Method) has been populated with sample data',
      life: TOAST_DURATION_DEFAULT_MS
    });
  }

  /**
   * Clear all form data
   */
  clearForm(): void {
    this.confirmationService.confirm({
      message: 'Are you sure you want to clear all data in this form?',
      header: 'Clear Form',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.form.reset();
        this.stateService.clearSheetData(SHEET_KEYS.statementOfCashFlowsDirectMethod);
        this.messageService.add({
          severity: 'info',
          summary: 'Form Cleared',
          detail: 'All form data has been cleared',
          life: TOAST_DURATION_DEFAULT_MS
        });
      }
    });
  }

  /**
   * Submit form data
   */
  submitForm(): void {
    if (this.reloadPromptDismissed()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Reload required',
        detail: 'Please refresh the page to obtain the latest version of this sheet.',
        life: TOAST_DURATION_LONG_MS
      });
      return;
    }

    if (this.form.invalid) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validation Error',
        detail: 'Please fill in all required fields',
        life: TOAST_DURATION_LONG_MS
      });
      return;
    }

    const formData = this.form.getRawValue();
    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // console.log('Role-17 Form submitted:', formData);

    this.messageService.add({
      severity: 'success',
      summary: 'Form Submitted',
      detail: 'Statement of Cash Flows (Direct Method) has been saved',
      life: TOAST_DURATION_DEFAULT_MS
    });
  }

  /**
   * Check if control is a header
   */
  isHeader(control: ControlConfig): boolean {
    return control.ui.control === 'header';
  }

  /**
   * Check if control is calculated
   */
  isCalculated(control: ControlConfig): boolean {
    return control.ui.calculated === true;
  }

  /**
   * Check if control has dual columns
   */
  hasDualColumn(control: ControlConfig): boolean {
    return control.ui.dualColumn === true;
  }

  /**
   * Get header level class
   */
  getHeaderClass(control: ControlConfig): string {
    if (control.ui.level === 1) return 'section-header-major';
    if (control.ui.level === 2) return 'section-header-minor';
    return '';
  }

  /**
   * Get row class for styling
   */
  getRowClass(control: ControlConfig): string {
    if (this.isCalculated(control)) {
      if (control.qname.includes('NetCash') || control.qname.includes('NetIncrease')) return 'total-row';
      return 'calculated-row';
    }
    return '';
  }

  /**
   * Run all validations, then trigger a single change detection at the end.
   * This prevents intermediate states where clearing errors in one method
   * temporarily render the template without errors from later validation methods.
   */
  private runAllValidations(): void {
    this.validationErrors.clear();

    this.validateAgainstApiData();

    // Single change detection after ALL validations complete
    this.validationErrors = new Map(this.validationErrors);
    this.cdr.detectChanges();
  }

  /**
   * Validate calculated totals against the N8N extraction snapshot.
   * Only validates calculated/readonly fields (Totals).
   * It does NOT call cdr.detectChanges() — the caller is responsible for triggering CD
   * after all validation methods have run.
   */
  // Developer Note (TODO): extracted-sum warning wired to the shared service, but
  // DORMANT + UNVERIFIED end-to-end — role17 (Cash Flows Direct) is gated out of the
  // active filing flow (status: not-in-use) AND its N8N snapshot is not seeded by
  // filing-form-container.populateSnapshotFromMbrsData. Re-test when the Direct method
  // is re-enabled and BE returns snapshot data for it.
  private validateAgainstApiData(): void {
    if (!this.schema) return;

    // role17 control names are `qname + wordSuffix` (word suffixes from
    // getColumnSuffixes(): GroupCurrent / GroupPrevious / CompanyCurrent /
    // CompanyPrevious / Current / Previous). The badge / tooltip helpers
    // (getRowValidationStatus / getRowErrorDescription) gate the display by
    // visible-column label, so these label strings must match the exact
    // strings those helpers render against: 'Group Current', 'Group Previous',
    // 'Company Current', 'Company Previous', 'Current', 'Previous'.
    const suffixes = this.getColumnSuffixes();
    const suffixLabels: Record<Role17ColumnSuffix, string> = {
      GroupCurrent: 'Group Current',
      GroupPrevious: 'Group Previous',
      CompanyCurrent: 'Company Current',
      CompanyPrevious: 'Company Previous',
      Current: 'Current',
      Previous: 'Previous',
    };
    const columns = suffixes.map((key) => ({ key, label: suffixLabels[key] }));

    const warnings = this.extractedSumWarning.computeMismatchWarnings({
      sheetKey: SHEET_KEYS.statementOfCashFlowsDirectMethod,
      controls: this.schema.controls,
      columns,
      getControlName: (qname, key) => qname + key,
      getCurrentValue: (name) => this.form.get(name)?.value,
    });

    this.extractedSumWarning.mergeIntoValidationErrors(this.validationErrors, this.schema.controls, warnings);
  }

  /**
   * Get validation status for a row
   * Returns: 'Failed' | 'Edited' | 'Valid*' | 'Valid' | ''
   */
  getRowValidationStatus(control: ControlConfig): 'Failed' | 'Edited' | 'Valid*' | 'Valid' | '' {
    // Only check monetary items
    if (control.type !== 'MonetaryItemType') return '';

    // Determine suffixes based on column configuration (shared helper).
    const suffixes = this.getColumnSuffixes();

    let hasInvalid = false;
    let hasEdited = false;
    let hasValue = false;

    for (const suffix of suffixes) {
      const controlName = control.qname + suffix;
      const formControl = this.form.get(controlName);

      if (formControl) {
        if (formControl.invalid && (formControl.dirty || formControl.touched)) hasInvalid = true;
        if (!formControl.pristine) hasEdited = true;
        if (formControl.value !== null && formControl.value !== undefined && formControl.value !== '') hasValue = true;
      }
    }

    if (hasInvalid) return 'Failed';
    if (hasEdited) return 'Edited';

    // Check if there are validation errors (ExtractedSum vs CalculatedSum mismatch)
    if (hasValue && this.validationErrors.has(control.qname)) {
      return 'Valid*';
    }

    if (hasValue) return 'Valid';
    return '';
  }

  /**
   * Get error description for a row (aggregates from all columns)
   */
  getRowErrorDescription(control: ControlConfig): string {
    // Only check monetary items
    if (control.type !== 'MonetaryItemType') return '';

    // Check validation errors first (from validateAgainstApiData)
    if (this.validationErrors.has(control.qname)) {
      const validationErrors = this.validationErrors.get(control.qname) || [];
      // Debug Log (Only enable when required to debug, do not allow this to land into prod)
      // console.log(`📝 getRowErrorDescription for ${control.qname}:`, validationErrors);
      // Format as numbered list
      return this.appendBaselineDebug(
        control,
        validationErrors
          .map((error, index) => `${index + 1}. ${error}`)
          .join('\n\n')
      );
    }

    // Determine suffixes and labels based on column configuration
    const columns: { suffix: string; label: string }[] = [];
    if (this.isConsolidated && this.hasPreviousYear) {
      columns.push(
        { suffix: 'GroupCurrent', label: 'Group Current' },
        { suffix: 'GroupPrevious', label: 'Group Previous' },
        { suffix: 'CompanyCurrent', label: 'Company Current' },
        { suffix: 'CompanyPrevious', label: 'Company Previous' }
      );
    } else if (this.isConsolidated && !this.hasPreviousYear) {
      columns.push(
        { suffix: 'GroupCurrent', label: 'Group Current' },
        { suffix: 'CompanyCurrent', label: 'Company Current' }
      );
    } else if (!this.isConsolidated && this.hasPreviousYear) {
      columns.push(
        { suffix: 'Current', label: 'Current' },
        { suffix: 'Previous', label: 'Previous' }
      );
    } else {
      columns.push({ suffix: 'Current', label: 'Current' });
    }

    const errors: string[] = [];

    for (const { suffix, label } of columns) {
      const controlName = control.qname + suffix;
      const formControl = this.form.get(controlName);

      if (formControl && formControl.invalid && (formControl.dirty || formControl.touched)) {
        const errorMessages: string[] = [];

        if (formControl.errors) {
          if (formControl.errors['required']) errorMessages.push('Required field');
          if (formControl.errors['min']) errorMessages.push(`Min: ${formControl.errors['min'].min}`);
          if (formControl.errors['max']) errorMessages.push(`Max: ${formControl.errors['max'].max}`);
        }

        if (errorMessages.length > 0) {
          errors.push(`${label}: ${errorMessages.join(', ')}`);
        }
      }
    }

    return this.appendBaselineDebug(control, errors.join('. '));
  }

  /**
   * Ctrl+Alt+B baseline debug overlay (non-production). Appended to whatever
   * real validation text this row already has (often none for a clean total).
   * baselineDebugForControl returns '' unless this row is a qualifying total.
   * Columns + getControlName rebuilt inline to mirror validateAgainstApiData's
   * computeMismatchWarnings call exactly (same suffixes + concat keys).
   */
  private appendBaselineDebug(control: ControlConfig, current: string): string {
    if (!this.baselineDebug.enabled()) return current;
    const suffixes = this.getColumnSuffixes();
    const suffixLabels: Record<Role17ColumnSuffix, string> = {
      GroupCurrent: 'Group Current',
      GroupPrevious: 'Group Previous',
      CompanyCurrent: 'Company Current',
      CompanyPrevious: 'Company Previous',
      Current: 'Current',
      Previous: 'Previous',
    };
    const columns = suffixes.map((key) => ({ key, label: suffixLabels[key] }));
    const baselineInfo = this.extractedSumWarning.baselineDebugForControl(
      SHEET_KEYS.statementOfCashFlowsDirectMethod,
      control as unknown as SummableControl,
      columns,
      (qname, key) => qname + key
    );
    if (!baselineInfo) return current;
    return current ? `${current}${baselineInfo}` : baselineInfo;
  }

  /**
   * Check if row has failed validation
   */
  hasRowFailed(control: ControlConfig): boolean {
    return this.getRowValidationStatus(control) === 'Failed';
  }

  /**
   * Check if row has validation error (for yellow highlighting)
   * Checks validationErrors Map directly instead of status
   */
  hasRowValidationError(control: ControlConfig): boolean {
    return this.validationErrors.has(control.qname);
  }
  /**
   * Does this field's label get a `*`?
   *
   * Delegates to the shared `isControlMandatory` so this sheet answers the
   * question the same way as every other.
   *
   * **Wired even though this sheet declares ZERO mandatory controls today.**
   * That is deliberate, and it is the lesson from roles 9, 13 and 15: they
   * were skipped during the 2026-08-11 marker rollout for having no mandatory
   * fields, and when the PO's enumerated totals list flagged their calculated
   * totals hours later the flags landed with nowhere to render — the `*`
   * silently did not appear until smoke testing caught it. A sheet that
   * renders schema-driven labels gets the marker regardless of its current
   * mandatory count, so the next list cannot reopen that gap. The PO's enumerated totals are recorded per field in the sheet's spec under `docs/specifications/sheets/`.
   *
   * `crossSheetValue` is supplied so a cross-sheet `requiredIf` resolves.
   * Same-sheet `selfData` is passed only by sheets keyed by bare qname; the
   * per-cell sheets (`qname[Dimension][Period]`) deliberately omit it, since a
   * same-sheet lookup would silently miss.
   */
  isFieldRequired(control: any): boolean {
    return isControlMandatory(control, {
      crossSheetValue: (sheet, field) =>
        (this.stateService.getSheetData(sheet as any) as any)?.[field],
    });
  }

  /**
   * Memoised per-control numeric policy — this sheet's single declaration
   * point for decimals, grouping and the integer-digit cap, all resolved from
   * the control's own schema record rather than hand-written per binding.
   *
   * Memoised because templates call this once per binding inside an `*ngFor`;
   * an unmemoised `resolveNumericPolicy` would re-resolve on every
   * change-detection cycle. Keyed by `qname`, unique per control in a sheet.
   *
   * `[min]`/`[max]` are deliberately NOT bound from here — `SchemaNumericDirective`
   * assigns them onto the PrimeNG instance directly. See that directive's class
   * docblock for why a template binding coerces an unconstrained `undefined`
   * to `NaN` and silently blocks the minus key on signed monetary fields.
   */
  private policyCache = new Map<string, NumericFieldPolicy>();

  numericPolicy(control: ControlConfig): NumericFieldPolicy {
    const cached = this.policyCache.get(control.qname);
    if (cached)
      return cached;
    const resolved = resolveNumericPolicy(control);
    this.policyCache.set(control.qname, resolved);
    return resolved;
  }

}
